import React, { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { History, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

const emptyVendor = {
  name: "",
  contact: "",
  address: "",
  gst_number: "",
};

export const VendorMaster = ({ vendors, onChanged, canDelete = false }) => {
  const [search, setSearch] = useState("");
  const [newVendor, setNewVendor] = useState(emptyVendor);
  const [editing, setEditing] = useState(null);
  const [historyVendor, setHistoryVendor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  const filteredVendors = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return vendors;
    }
    return vendors.filter((vendor) =>
      [vendor.name, vendor.contact, vendor.address, vendor.gst_number]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [search, vendors]);

  const addVendor = async () => {
    if (!newVendor.name.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    try {
      setSaving(true);
      await api.post("/assets/vendors", {
        ...newVendor,
        name: newVendor.name.trim(),
        contact: newVendor.contact.trim() || null,
        address: newVendor.address.trim() || null,
        gst_number: newVendor.gst_number.trim() || null,
      });
      setNewVendor(emptyVendor);
      toast.success("Vendor added");
      onChanged();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editing?.name.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/assets/vendors/${editing.id}`, {
        name: editing.name.trim(),
        contact: editing.contact?.trim() || null,
        address: editing.address?.trim() || null,
        gst_number: editing.gst_number?.trim() || null,
      });
      setEditing(null);
      toast.success("Vendor updated");
      onChanged();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (vendor) => {
    try {
      await api.patch(`/assets/vendors/${vendor.id}`, { active: vendor.active === false });
      toast.success(vendor.active === false ? "Vendor activated" : "Vendor deactivated");
      onChanged();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const deleteVendor = async () => {
    if (!editing) {
      return;
    }
    try {
      setSaving(true);
      await api.delete(`/assets/vendors/${editing.id}`);
      setConfirmDelete(false);
      setEditing(null);
      toast.success("Vendor deleted. Existing purchase snapshots remain available.");
      onChanged();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="vendor-master-screen">
      <Card className="p-4 border-slate-200">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="section-sub font-semibold text-slate-800">Vendor Master</div>
            <div className="text-xs text-slate-500 mt-1">
              Managed suppliers remain traceable through their purchase snapshots and audit history.
            </div>
          </div>
          <div className="relative w-full lg:max-w-sm">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-10 pl-9"
              placeholder="Search name, contact, address, GST"
              data-testid="vendor-master-search"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 mt-5 md:grid-cols-2 xl:grid-cols-5">
          <div>
            <Label>Name *</Label>
            <Input
              value={newVendor.name}
              onChange={(event) => setNewVendor({ ...newVendor, name: event.target.value })}
              className="h-11 mt-1"
              data-testid="vendor-master-name-input"
            />
          </div>
          <div>
            <Label>Contact</Label>
            <Input
              value={newVendor.contact}
              onChange={(event) => setNewVendor({ ...newVendor, contact: event.target.value })}
              className="h-11 mt-1"
              data-testid="vendor-master-contact-input"
            />
          </div>
          <div className="xl:col-span-2">
            <Label>Address</Label>
            <Input
              value={newVendor.address}
              onChange={(event) => setNewVendor({ ...newVendor, address: event.target.value })}
              className="h-11 mt-1"
              data-testid="vendor-master-address-input"
            />
          </div>
          <div>
            <Label>GST No.</Label>
            <Input
              value={newVendor.gst_number}
              onChange={(event) =>
                setNewVendor({ ...newVendor, gst_number: event.target.value.toUpperCase() })
              }
              className="h-11 mt-1"
              data-testid="vendor-master-gst-input"
            />
          </div>
        </div>
        <Button
          onClick={addVendor}
          disabled={saving}
          className="h-11 mt-4 bg-slate-900 text-white hover:bg-slate-800"
          data-testid="vendor-master-add-button"
        >
          Add Vendor
        </Button>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="vr-table" data-testid="vendor-master-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Address</th>
                <th>GST No.</th>
                <th>Status</th>
                <th className="no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.map((vendor) => (
                <tr key={vendor.id} data-testid={`vendor-master-row-${vendor.id}`}>
                  <td className="font-medium">{vendor.name}</td>
                  <td>{vendor.contact || vendor.phone || vendor.email || "—"}</td>
                  <td className="max-w-[260px] whitespace-normal">{vendor.address || "—"}</td>
                  <td className="font-mono text-xs">{vendor.gst_number || "—"}</td>
                  <td data-testid={`vendor-master-status-${vendor.id}`}>
                    <Badge
                      className={
                        vendor.active === false
                          ? "bg-slate-100 border text-slate-700"
                          : "bg-emerald-100 border text-emerald-800"
                      }
                    >
                      {vendor.active === false ? "Inactive" : "Active"}
                    </Badge>
                  </td>
                  <td className="no-print">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ ...vendor })}
                        data-testid={`vendor-master-edit-${vendor.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleStatus(vendor)}
                        data-testid={`vendor-master-toggle-${vendor.id}`}
                      >
                        {vendor.active === false ? "Activate" : "Deactivate"}
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => setHistoryVendor(vendor)}
                        data-testid={`vendor-master-history-${vendor.id}`}
                        title="View audit history"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredVendors.length && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-500" data-testid="vendor-master-empty">
                    No vendors match this search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDelete(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent className="max-w-xl" data-testid="vendor-master-edit-dialog">
          <DialogHeader><DialogTitle>Edit Vendor</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Name *</Label>
                <Input
                  value={editing.name || ""}
                  onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                  className="h-11 mt-1"
                  data-testid="vendor-master-edit-name"
                />
              </div>
              <div>
                <Label>Contact</Label>
                <Input
                  value={editing.contact || ""}
                  onChange={(event) => setEditing({ ...editing, contact: event.target.value })}
                  className="h-11 mt-1"
                  data-testid="vendor-master-edit-contact"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Address</Label>
                <Textarea
                  value={editing.address || ""}
                  onChange={(event) => setEditing({ ...editing, address: event.target.value })}
                  data-testid="vendor-master-edit-address"
                />
              </div>
              <div>
                <Label>GST No.</Label>
                <Input
                  value={editing.gst_number || ""}
                  onChange={(event) =>
                    setEditing({ ...editing, gst_number: event.target.value.toUpperCase() })
                  }
                  className="h-11 mt-1"
                  data-testid="vendor-master-edit-gst"
                />
              </div>
            </div>
          )}
          {confirmDelete ? (
            <div className="border border-red-200 bg-red-50 p-3" data-testid="vendor-master-delete-confirmation">
              <div className="text-sm text-red-800">
                Delete this vendor from the master list? Existing purchase snapshots will remain unchanged.
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(false)}
                  data-testid="vendor-master-delete-cancel"
                >
                  Keep Vendor
                </Button>
                <Button
                  onClick={deleteVendor}
                  disabled={saving}
                  className="bg-red-700 text-white hover:bg-red-800"
                  data-testid="vendor-master-delete-confirm"
                >
                  Delete Vendor
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-between gap-2">
              {canDelete && (
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(true)}
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="vendor-master-delete-button"
                >
                  Delete Vendor
                </Button>
              )}
              <Button
                onClick={saveEdit}
                disabled={saving}
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="vendor-master-save-edit"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyVendor} onOpenChange={(open) => !open && setHistoryVendor(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="vendor-master-history-dialog">
          <DialogHeader><DialogTitle>Vendor Audit History</DialogTitle></DialogHeader>
          <div className="space-y-3" data-testid="vendor-master-history-list">
            {(historyVendor?.history || []).slice().reverse().map((entry, index) => (
              <div key={`${entry.at}-${index}`} className="border border-slate-200 p-3 text-sm">
                <div className="font-medium text-slate-800">{entry.event}</div>
                <div className="text-xs text-slate-500 mt-1">{entry.at} · {entry.by_name || "System"}</div>
                <div className="grid grid-cols-1 gap-2 mt-3 sm:grid-cols-2">
                  <pre className="overflow-x-auto bg-slate-50 p-2 text-xs">Before: {JSON.stringify(entry.before)}</pre>
                  <pre className="overflow-x-auto bg-slate-50 p-2 text-xs">After: {JSON.stringify(entry.after)}</pre>
                </div>
              </div>
            ))}
            {!historyVendor?.history?.length && (
              <div className="text-sm text-slate-500" data-testid="vendor-master-history-empty">
                No recorded vendor changes yet.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};